import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Search,
    Filter,
    Calendar,
    User,
    ChevronDown,
    Download,
    FileText,
    History,
    Wallet,
    Info,
    AlertCircle,
    ArrowUpRight,
    ArrowDownRight,
    Clock,
    CheckCircle,
    XCircle,
    RotateCcw,
    Eye,
    TrendingUp,
    Award,
    RefreshCw,
    PlusCircle,
    Save,
    X,
    Upload, CalendarDays,
    ShieldCheck,
} from "lucide-react";

import { getAllLeaveRequestsRequest, exportLeaveRequestsExcelRequest, createLeaveRequestRequest } from "../../../../redux/actions/leaveRequestsActions";

import {
    getAllCompanySoldesRequest,
    exportSoldesExcelRequest,
    importSoldesExcelRequest,
    updateSoldeRequest,
    getAllMovementsRequest,
    bulkAdjustSoldesRequest
} from "../../../../redux/actions/leaveBalanceActions";
import { fetchParamCongesRequest } from "../../../../redux/actions/paramCongesActions";

import "../../Mesrequetes/MesrequetesStyles/DemandeConges.css";
import "./SoldeCongesManagement.css";

/**
 * ── SearchableCombobox ──
 * Champ hybride : liste déroulante (options prédéfinies) + saisie libre pour
 * rechercher/filtrer. L'utilisateur peut cliquer pour ouvrir la liste,
 * taper pour filtrer les suggestions, sélectionner une suggestion,
 * ou simplement continuer à taper un texte libre (comme avant).
 */
const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    // Garde le texte affiché synchronisé si la valeur change de l'extérieur
    // (ex: bouton "Réinitialiser")
    useEffect(() => {
        setQuery(value || "");
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => {
        setQuery(opt.label);
        onChange(opt.value);
        setOpen(false);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        onChange(val);
        setOpen(true);
    };

    const handleClear = () => {
        setQuery("");
        onChange("");
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="dcInput"
                    style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button
                            type="button"
                            onClick={handleClear}
                            style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }}
                            title="Effacer"
                        >
                            <X size={13} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }}
                        title="Afficher la liste"
                    >
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>

            {open && filteredOptions.length > 0 && (
                <div
                    style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto",
                    }}
                >
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div
                                key={opt.value}
                                onClick={() => handleSelect(opt)}
                                style={{
                                    padding: "8px 12px", fontSize: 13, cursor: "pointer",
                                    background: isSelected ? "#eff6ff" : "#fff",
                                    color: isSelected ? "#204385" : "#374151",
                                    fontWeight: isSelected ? 600 : 400,
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}
                            >
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}

            {open && query && filteredOptions.length === 0 && (
                <div
                    style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px",
                        fontSize: 12, color: "#9ca3af",
                    }}
                >
                    Aucun résultat — la recherche libre reste appliquée
                </div>
            )}
        </div>
    );
};

const SoldeCongesManagement = () => {
    const dispatch = useDispatch();

    // ── Redux state ──
    const allRequests = useSelector((state) => state.leaveRequests?.allRequests || []);
    const companySoldes = useSelector((state) => state.leaveBalance?.companySoldes || []);
    const movements = useSelector((state) => state.leaveBalance?.movements || []);
    const loading = useSelector((state) => state.leaveRequests?.loading || state.leaveBalance?.loading);

    const paramState = useSelector((state) => state.paramConges || {});
    const typesAbsences = paramState.typesAbsences || [];

    // ── UI State ──
    const [activeTab, setActiveTab] = useState("historique");
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCollabMatricule, setFilterCollabMatricule] = useState("");
    const [filterEntite, setFilterEntite] = useState("");
    const [filterDateOp, setFilterDateOp] = useState("");
    const [filterSoldeRestant, setFilterSoldeRestant] = useState("");

    const handleResetFilters = () => {
        setFilterCollabMatricule("");
        setFilterEntite("");
        setFilterDateOp("");
        setFilterSoldeRestant("");
        setSearchTerm("");
    };

    const fileInputRef = useRef(null);

    // ── Adjustment Modal ──
    const [adjustModalOpen, setAdjustModalOpen] = useState(false);
    const [selectedSolde, setSelectedSolde] = useState(null);
    const [daysToAdd, setDaysToAdd] = useState("");
    const [motifType, setMotifType] = useState("");
    const [commentaireLibre, setCommentaireLibre] = useState("");

    // ── Alimentation groupée des soldes ──
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkAbsenceTypeId, setBulkAbsenceTypeId] = useState("");
    const [bulkSelectedUserIds, setBulkSelectedUserIds] = useState([]);
    const [bulkDays, setBulkDays] = useState("");
    const [bulkMotif, setBulkMotif] = useState("");
    const [bulkComment, setBulkComment] = useState("");
    const [bulkUserSearch, setBulkUserSearch] = useState("");
    const [bulkError, setBulkError] = useState("");

    const MOTIFS_PREDEFINED = [
        { value: "Régularisation de solde",       label: "Régularisation de solde" },
        { value: "Jour férié non décompté",        label: "Jour férié non décompté" },
        { value: "Acquisition exceptionnelle",     label: "Acquisition exceptionnelle" },
        { value: "Correction technique",           label: "Correction technique" },
        { value: "Report de congés N-1",           label: "Report de congés N-1" },
        { value: "Déduction pour absence injustifiée", label: "Déduction pour absence injustifiée" },
        { value: "Déduction weekend travaillé",    label: "Déduction weekend travaillé" },
        { value: "Prime de présence",              label: "Prime de présence" },
    ];

    // ── Saisir un congé Modal ──
    const [saisirModalOpen, setSaisirModalOpen] = useState(false);
    const [saisirForm, setSaisirForm] = useState({
        absenceTypeId: "",
        dateDebut: "",
        dateFin: "",
        commentaire: "",
        justificatif: null,
        autorisation: null,
        selectedUserId: "",
    });
    const [saisirError, setSaisirError] = useState("");
    const [userSearch, setUserSearch] = useState("");

    // ── Init ──
    useEffect(() => {
        dispatch(getAllLeaveRequestsRequest());
        dispatch(getAllMovementsRequest());
        dispatch(fetchParamCongesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (filterDateOp) {
            dispatch(getAllCompanySoldesRequest({ date: filterDateOp }));
        } else {
            dispatch(getAllCompanySoldesRequest());
        }
    }, [filterDateOp, dispatch]);

    // ── Helpers ──
    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const calculerDuree = (debut, fin) => {
        if (!debut || !fin) return 0;
        return Math.ceil((new Date(fin) - new Date(debut)) / (1000 * 60 * 60 * 24)) + 1;
    };

    const getAbsenceTypeLabel = (t) =>
        t?.label || t?.libelle || t?.name || t?.nom || "Type";

    const getAbsenceTypeColor = (t) =>
        t?.couleur || t?.color || "#3b82f6";

    // ── Types de congés disponibles ──
    const typesCongesDisponibles = useMemo(() => {
        return (typesAbsences || []).map((t) => ({
            id: t?.id,
            label: getAbsenceTypeLabel(t),
            couleur: getAbsenceTypeColor(t),
            requireJustificatif: !!t?.justificationRequired,
            justificatifMandatory: !!t?.justificationMandatory,
            justificationType: t?.justificationType || "Justificatif",
            requireAutorisation: !!t?.requireAutorisation || !!t?.isAutorisationRequired,
            category: t?.category,
            nbJours: t?.nbJours,
        }));
    }, [typesAbsences]);

    const typesSoldesDisponibles = useMemo(
        () => typesCongesDisponibles.filter((type) => type.category === "ANNUAL"),
        [typesCongesDisponibles]
    );

    const saisirTypeObj = useMemo(() => {
        return typesCongesDisponibles.find(
            (t) => String(t.id) === String(saisirForm.absenceTypeId)
        ) || null;
    }, [saisirForm.absenceTypeId, typesCongesDisponibles]);

    // ── Utilisateurs uniques depuis companySoldes ──
    const isUserActif = (s) => s && s.actif !== false && s.actif !== "false" && s.userActif !== false && s.userEnabled !== false && s.statut !== "INACTIF" && s.userStatut !== "INACTIF";

    const uniqueUsers = useMemo(() => {
        const seen = new Set();
        return companySoldes
            .filter((s) => {
                if (!isUserActif(s)) return false;
                if (seen.has(s.userId)) return false;
                // ✅ Uniquement les collaborateurs ayant activé la délégation
                if (s.isLeaveDelegationEnabled !== true) return false;
                seen.add(s.userId);
                return true;
            })

            .map((s) => ({
                id: s.userId,
                fullName: s.userFullName,
                employeeCode: s.userEmployeeCode,
            }));
    }, [companySoldes]);

    const filteredUsers = useMemo(() => {
        if (!userSearch) return uniqueUsers;
        const s = userSearch.toLowerCase();
        return uniqueUsers.filter(
            (u) =>
                u.fullName?.toLowerCase().includes(s) ||
                u.employeeCode?.toLowerCase().includes(s)
        );
    }, [uniqueUsers, userSearch]);

    const bulkUsers = useMemo(() => {
        const users = new Map();
        companySoldes.forEach((solde) => {
            if (isUserActif(solde) && solde.userId && !users.has(solde.userId)) {
                users.set(solde.userId, { id: solde.userId, fullName: solde.userFullName, employeeCode: solde.userEmployeeCode });
            }
        });
        return Array.from(users.values()).sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
    }, [companySoldes]);

    const filteredBulkUsers = useMemo(() => {
        const query = bulkUserSearch.trim().toLowerCase();
        if (!query) return bulkUsers;
        return bulkUsers.filter((user) => user.fullName?.toLowerCase().includes(query) || user.employeeCode?.toLowerCase().includes(query));
    }, [bulkUsers, bulkUserSearch]);

    // ── Options pour les filtres "liste déroulante + saisie libre" ──
    // Les filtres sont liés entre eux : si une entité est sélectionnée, la liste
    // des collaborateurs ne propose que ceux de cette entité (et inversement).
    const collaborateurOptions = useMemo(() => {
        const entiteVal = filterEntite ? filterEntite.toLowerCase() : "";
        const matchesEntite = (entityName) =>
            !entiteVal || (entityName || "").toLowerCase().includes(entiteVal);

        const map = new Map();
        (companySoldes || []).forEach((s) => {
            if (s.userFullName && matchesEntite(s.entityName) && !map.has(s.userFullName)) {
                map.set(s.userFullName, {
                    value: s.userFullName,
                    label: s.userEmployeeCode ? `${s.userFullName} (${s.userEmployeeCode})` : s.userFullName,
                });
            }
        });
        (allRequests || []).forEach((r) => {
            if (r.employeeName && matchesEntite(r.entityName) && !map.has(r.employeeName)) {
                map.set(r.employeeName, {
                    value: r.employeeName,
                    label: r.employeeCode ? `${r.employeeName} (${r.employeeCode})` : r.employeeName,
                });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [companySoldes, allRequests, filterEntite]);

    const entiteOptions = useMemo(() => {
        const collabVal = filterCollabMatricule ? filterCollabMatricule.toLowerCase() : "";
        const matchesCollab = (name, code) =>
            !collabVal ||
            (name || "").toLowerCase().includes(collabVal) ||
            (code || "").toLowerCase().includes(collabVal);

        const set = new Set();
        (companySoldes || []).forEach((s) => {
            if (s.entityName && matchesCollab(s.userFullName, s.userEmployeeCode)) set.add(s.entityName);
        });
        (allRequests || []).forEach((r) => {
            if (r.entityName && matchesCollab(r.employeeName, r.employeeCode)) set.add(r.entityName);
        });
        return Array.from(set).sort().map((e) => ({ value: e, label: e }));
    }, [companySoldes, allRequests, filterCollabMatricule]);

    // ── Export / Import ──
    const handleExport = () => {
        if (activeTab === "soldes") {
            dispatch(exportSoldesExcelRequest());
        } else {
            dispatch(exportLeaveRequestsExcelRequest("all_history"));
        }
    };

    const handleImportClick = () => fileInputRef.current?.click();

    const handleImportFile = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            dispatch(importSoldesExcelRequest(file));
            e.target.value = "";
        }
    };

    // ── Adjustment Modal handlers ──
    const handleOpenAdjust = (solde) => {
        setSelectedSolde(solde);
        setDaysToAdd("");
        setMotifType("");
        setCommentaireLibre("");
        setAdjustModalOpen(true);
    };

    const handleCloseAdjust = () => {
        setAdjustModalOpen(false);
        setSelectedSolde(null);
        setMotifType("");
        setCommentaireLibre("");
    };

    const handleSaveAdjustment = () => {
        if (!selectedSolde || !daysToAdd) return;
        const add = parseFloat(daysToAdd);
        if (isNaN(add)) return;

        const currentAjoute = selectedSolde.soldeAjoute || 0;
        const newAjoute = currentAjoute + add;

        dispatch(updateSoldeRequest(selectedSolde.id, {
            soldeAcquis: selectedSolde.soldeAcquis,
            soldeConsomme: selectedSolde.soldeConsomme,
            soldeAjoute: newAjoute,
            actif: true,
            motifType: motifType || null,
            commentaireLibre: commentaireLibre || null,
        }));
        handleCloseAdjust();
    };

    const handleOpenBulkAdjustment = () => {
        setBulkAbsenceTypeId("");
        setBulkSelectedUserIds(bulkUsers.map((user) => user.id));
        setBulkDays("");
        setBulkMotif("");
        setBulkComment("");
        setBulkUserSearch("");
        setBulkError("");
        setBulkModalOpen(true);
    };

    const handleBulkAdjustmentSubmit = () => {
        const jours = Number(String(bulkDays).replace(",", "."));
        if (!bulkAbsenceTypeId) return setBulkError("Veuillez sélectionner un type de congé.");
        if (!Number.isFinite(jours) || jours === 0) return setBulkError("Saisissez un nombre de jours différent de zéro.");
        if (bulkSelectedUserIds.length === 0) return setBulkError("Sélectionnez au moins un collaborateur.");

        const latestSoldesByUser = new Map();
        companySoldes
            .filter((solde) => solde.actif && bulkSelectedUserIds.includes(solde.userId) && String(solde.absenceTypeId) === String(bulkAbsenceTypeId))
            .forEach((solde) => {
                const current = latestSoldesByUser.get(solde.userId);
                if (!current || solde.annee > current.annee) latestSoldesByUser.set(solde.userId, solde);
            });
        const soldeIds = Array.from(latestSoldesByUser.values()).map((solde) => solde.id);
        if (soldeIds.length === 0) return setBulkError("Aucun solde correspondant au type sélectionné n'a été trouvé.");

        dispatch(bulkAdjustSoldesRequest({ soldeIds, jours, motifType: bulkMotif || null, commentaireLibre: bulkComment || null }));
        setBulkModalOpen(false);
    };

    // ── Saisir un congé Modal handlers ──
    const handleCloseSaisir = () => {
        setSaisirModalOpen(false);
        setSaisirForm({
            absenceTypeId: "",
            dateDebut: "",
            dateFin: "",
            commentaire: "",
            justificatif: null,
            autorisation: null,
            selectedUserId: "",
        });
        setSaisirError("");
        setUserSearch("");
    };

    const handleSaisirSubmit = () => {
        setSaisirError("");

        if (!saisirForm.absenceTypeId) {
            setSaisirError("Veuillez sélectionner un type de congé.");
            return;
        }
        if (!saisirForm.dateDebut || !saisirForm.dateFin) {
            setSaisirError("Veuillez renseigner la date de début et la date de fin.");
            return;
        }
        if (!saisirForm.selectedUserId) {
            setSaisirError("Veuillez sélectionner un collaborateur.");
            return;
        }

        const payload = {
            absenceTypeId: Number(saisirForm.absenceTypeId),
            dateDebut: saisirForm.dateDebut,
            dateFin: saisirForm.dateFin,
            commentaire: saisirForm.commentaire,
            employeeId: saisirForm.selectedUserId, // L'ID du collaborateur bénéficiaire
            requestType: "conge", // Type par défaut pour ce module
            justificatif: saisirForm.justificatif,
            autorisation: saisirForm.autorisation,
        };

        const executeSubmission = (excessDaysDeductionSource = null) => {
            dispatch(createLeaveRequestRequest({ ...payload, excessDaysDeductionSource }));
            handleCloseSaisir();
        };

        if (saisirTypeObj?.category === "SPECIAL"
            && saisirTypeObj.nbJours !== null
            && saisirTypeObj.nbJours !== undefined
            && dureeCalculee > Number(saisirTypeObj.nbJours)) {
            const excessDays = dureeCalculee - Number(saisirTypeObj.nbJours);
            Swal.fire({
                title: "Jours autorisés dépassés",
                html: `
                    <div class="dcExcessDays">
                        <div class="dcExcessDays__summary">
                            <span>Autorisés <strong>${saisirTypeObj.nbJours} j</strong></span>
                            <span>Demandés <strong>${dureeCalculee} j</strong></span>
                            <span class="dcExcessDays__excess">À traiter <strong>${excessDays} j</strong></span>
                        </div>
                        <p class="dcExcessDays__prompt">Choisissez le traitement des jours dépassant la limite :</p>
                        <label class="dcExcessDays__choice is-selected">
                            <input type="radio" name="excess-days-source" value="SALARY" checked />
                            <span class="dcExcessDays__radio"></span>
                            <span><strong>Déduire du salaire</strong><small>Les jours dépassant la limite seront transmis pour retenue sur salaire.</small></span>
                        </label>
                        <label class="dcExcessDays__choice">
                            <input type="radio" name="excess-days-source" value="LEAVE_BALANCE" />
                            <span class="dcExcessDays__radio"></span>
                            <span><strong>Déduire du solde de congé</strong><small>Une demande annuelle complémentaire sera créée pour les jours excédentaires.</small></span>
                        </label>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: "Confirmer la demande",
                cancelButtonText: "Annuler",
                confirmButtonColor: "#204385",
                cancelButtonColor: "#64748b",
                customClass: { popup: "dcExcessDaysPopup", confirmButton: "dcExcessDaysConfirm" },
                didOpen: () => {
                    const choices = Swal.getPopup().querySelectorAll(".dcExcessDays__choice");
                    choices.forEach((choice) => choice.addEventListener("click", () => {
                        choices.forEach((item) => item.classList.remove("is-selected"));
                        choice.classList.add("is-selected");
                        choice.querySelector("input").checked = true;
                    }));
                },
                preConfirm: () => Swal.getPopup().querySelector('input[name="excess-days-source"]:checked')?.value,
            }).then((result) => {
                if (result.isConfirmed && result.value) executeSubmission(result.value);
            });
            return;
        }

        executeSubmission();
    };


    // ── Filtered data ──
    const historyRequests = useMemo(() => {
        let list = (allRequests || []).filter((req) => {
            const s = (req.status || req.statut || "").toUpperCase();
            return (
                s !== "PENDING" &&
                s !== "PENDING_MANAGER" &&
                s !== "PENDING_ADMIN" &&
                s !== "EN_ATTENTE"
            );
        });
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter(
                (r) =>
                    (r.employeeName || "").toLowerCase().includes(s) ||
                    (r.employeeCode || "").toLowerCase().includes(s) ||
                    (r.absenceTypeNom || "").toLowerCase().includes(s)
            );
        }
        return list;
    }, [allRequests, searchTerm]);

    const historyMovements = useMemo(() => {
        let list = movements || [];
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter(
                (m) =>
                    (m.employeeName || "").toLowerCase().includes(s) ||
                    (m.employeeCode || "").toLowerCase().includes(s) ||
                    (m.absenceTypeNom || "").toLowerCase().includes(s) ||
                    (m.reference || "").toLowerCase().includes(s) ||
                    (m.typeMouvement || "").toLowerCase().includes(s)
            );
        }
        return list;
    }, [movements, searchTerm]);

    const combinedHistoryAll = useMemo(() => {
        // 1. Demandes approuvées / annulées / reportées de TOUS les collaborateurs
        const filteredRequests = (allRequests || []).filter((req) => {
            const s = (req.status || req.statut || "").toUpperCase();
            return (
                s === "APPROVED" || s === "APPROUVE" ||
                s === "CANCELLED" || s === "ANNULE" ||
                s === "POSTPONED" || s === "REPORTE"
            );
        });

        const requestsAsMovements = filteredRequests.map((req) => {
            const deb = req.dateDebut || req.startDate || req.fromDate;
            const fin = req.dateFin || req.endDate || req.toDate;
            const dateStr = `Du ${formatDate(deb)} au ${formatDate(fin)}`;
            const motifText = req.commentaire ? `${dateStr} - ${req.commentaire}` : dateStr;
            const valeur = -Number(req.duree || calculerDuree(deb, fin) || 0);

            const statusRaw = (req.status || req.statut || "").toUpperCase();
            const statut =
                statusRaw === "APPROVED" || statusRaw === "APPROUVE" ? "approuve" :
                    statusRaw === "CANCELLED" || statusRaw === "ANNULE" ? "annule" :
                        statusRaw === "POSTPONED" || statusRaw === "REPORTE" ? "reporte" : "approuve";

            const refText = req.demandeurName
                ? `${req.demandeurName} (${req.demandeurCode || "—"})`
                : `REF-${req.id}`;

            return {
                id: `req-${req.id}`,
                employeeName: req.employeeName,
                employeeCode: req.employeeCode,
                entityName: req.entityName,
                dateMouvement: req.createdAt || req.dateDebut,
                reference: refText,
                motif: motifText,
                commentaire: req.motifRejet ? `Motif: ${req.motifRejet}` : "",
                absenceTypeNom: req.absenceTypeNom || req.absenceTypeName || "Congé",
                absenceTypeCouleur: req.absenceTypeCouleur || "#3b82f6",
                soldeInitialSnapshot: req.soldeInitial ?? null,
                soldeRestantSnapshot: req.soldeRestant ?? null,
                valeur,
                isLeaveRequest: true,
                statut,
            };
        });

        // 2. Filtrer les mouvements pour ne garder que ceux non liés à une demande (exclure CONSUMPTION)
        const positiveMovements = (movements || [])
            .filter((m) => m.typeMouvement !== "CONSUMPTION")
            .map((m) => ({
                id: `mov-${m.id}`,
                employeeName: m.employeeName,
                employeeCode: m.employeeCode,
                entityName: m.entityName,
                dateMouvement: m.dateMouvement,
                reference: m.crediteurName
                    ? `${m.crediteurName} (${m.crediteurMatricule || "—"})`
                    : "SYS_AUTO",
                motif: m.commentaire || m.motif || "Ajustement",
                commentaire: "",
                absenceTypeNom: m.absenceTypeNom,
                absenceTypeCouleur: m.absenceTypeCouleur,
                soldeInitialSnapshot: m.soldeInitialSnapshot ?? null,
                soldeRestantSnapshot: m.soldeRestantSnapshot ?? null,
                valeur: Number(m.valeur || 0),
                isLeaveRequest: false,
                statut: "valide",
            }));

        // 3. Fusion + tri par date décroissante
        return [...requestsAsMovements, ...positiveMovements].sort((a, b) => {
            const da = a.dateMouvement ? new Date(a.dateMouvement).getTime() : 0;
            const db = b.dateMouvement ? new Date(b.dateMouvement).getTime() : 0;
            return db - da;
        });
    }, [allRequests, movements]);

    const filteredCombinedHistory = useMemo(() => {
        let list = combinedHistoryAll;

        if (filterCollabMatricule) {
            const val = filterCollabMatricule.toLowerCase();
            list = list.filter(item =>
                (item.employeeName || "").toLowerCase().includes(val) ||
                (item.employeeCode || "").toLowerCase().includes(val)
            );
        }
        if (filterEntite) {
            const val = filterEntite.toLowerCase();
            list = list.filter(item =>
                (item.entityName || "").toLowerCase().includes(val)
            );
        }
        if (filterDateOp) {
            list = list.filter(item => {
                if (!item.dateMouvement) return false;
                const d = new Date(item.dateMouvement).toISOString().split("T")[0];
                return d === filterDateOp;
            });
        }
        if (filterSoldeRestant) {
            const limit = parseFloat(filterSoldeRestant);
            if (!isNaN(limit)) {
                list = list.filter(item =>
                    item.soldeRestantSnapshot !== null && item.soldeRestantSnapshot >= limit
                );
            }
        }
        return list;
    }, [combinedHistoryAll, filterCollabMatricule, filterEntite, filterDateOp, filterSoldeRestant]);

    const flattenedSoldes = useMemo(() => {
        let list = companySoldes;

        if (filterCollabMatricule) {
            const val = filterCollabMatricule.toLowerCase();
            list = list.filter(s =>
                (s.userFullName || "").toLowerCase().includes(val) ||
                (s.userEmployeeCode || "").toLowerCase().includes(val)
            );
        }
        if (filterEntite) {
            const val = filterEntite.toLowerCase();
            list = list.filter(s => (s.entityName || "").toLowerCase().includes(val));
        }
        if (filterSoldeRestant) {
            const limit = parseFloat(filterSoldeRestant);
            if (!isNaN(limit)) {
                list = list.filter(s => s.soldeRestant !== null && s.soldeRestant >= limit);
            }
        }
        return list;
    }, [companySoldes, filterCollabMatricule, filterEntite, filterSoldeRestant]);

    const dureeCalculee = calculerDuree(saisirForm.dateDebut, saisirForm.dateFin);
    const selectedUser = uniqueUsers.find(
        (u) => String(u.id) === String(saisirForm.selectedUserId)
    );

    return (
        <div className="dcPage scmPage">
            <div className="dcWrap dcMax">

                {/* ── Header ── */}
                <div className="dcHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                        <h1>Gestion des soldes de congés</h1>
                        <p>Consultez et ajustez les soldes de congés de vos collaborateurs.</p>
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <button
                            className="dcBtn dcBtn--outline"
                            style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", marginTop: 4, padding: "10px 18px", fontSize: 14, fontWeight: 600 }}
                            onClick={handleOpenBulkAdjustment}
                        >
                            <Wallet size={16} /> Alimenter les soldes
                        </button>
                        <button
                            className="dcBtn dcBtn--primary"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                whiteSpace: "nowrap",
                                marginTop: 4,
                                padding: "10px 18px",
                                fontSize: 14,
                                fontWeight: 600,
                            }}
                            onClick={() => setSaisirModalOpen(true)}
                        >
                            <PlusCircle size={16} />
                            Saisir un congé
                        </button>
                    </div>
                </div>

                <div className="dcCard">
                    {/* ── Tabs ── */}
                    <div className="dcTabs">
                        <button
                            className={`dcTab ${activeTab === "historique" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("historique")}
                        >
                            <History size={16} /> États des congés
                        </button>
                        <button
                            className={`dcTab ${activeTab === "soldes" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("soldes")}
                        >
                            <Wallet size={16} /> Gestion des soldes de congés
                        </button>
                    </div>

                    <div className="dcBody">
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept=".xlsx,.xls"
                            style={{ display: "none" }}
                            onChange={handleImportFile}
                        />

                        {/* ── Filters Panel ── */}
                        <div
                            className="scmFiltersPanel"
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "flex-end",
                                gap: "16px",
                                marginBottom: "20px",
                                padding: "16px",
                                background: "#f8fafc",
                                borderRadius: "10px",
                                border: "1px solid #e2e8f0",
                            }}
                        >
                            <div className="filterGroup" style={{minWidth: 220, flex: "1 1 220px"}}>
                                <label style={{
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#64748b",
                                    display: "block",
                                    marginBottom: "4px"
                                }}>
                                    Collaborateur / Matricule
                                </label>
                                <SearchableCombobox
                                    value={filterCollabMatricule}
                                    onChange={setFilterCollabMatricule}
                                    options={collaborateurOptions}
                                    placeholder="Nom ou matricule..."
                                />
                            </div>
                            <div className="filterGroup" style={{minWidth: 180, flex: "1 1 180px"}}>
                                <label style={{
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#64748b",
                                    display: "block",
                                    marginBottom: "4px"
                                }}>Entité</label>
                                <SearchableCombobox
                                    value={filterEntite}
                                    onChange={setFilterEntite}
                                    options={entiteOptions}
                                    placeholder="Entité..."
                                />
                            </div>
                            <div className="filterGroup" style={{minWidth: 160, flex: "1 1 160px"}}>
                                <label style={{
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#64748b",
                                    display: "block",
                                    marginBottom: "4px"
                                }}>Solde à la date du</label>
                                <input
                                    type="date"
                                    value={filterDateOp}
                                    onChange={(e) => setFilterDateOp(e.target.value)}
                                    className="dcInput"
                                    style={{width: "100%", fontSize: "13px"}}
                                />
                            </div>
                            <div className="filterGroup" style={{ minWidth: 140, flex: "1 1 140px" }}>
                                <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Solde restant min</label>
                                <input
                                    type="number"
                                    placeholder="Ex: 5"
                                    value={filterSoldeRestant}
                                    onChange={(e) => setFilterSoldeRestant(e.target.value)}
                                    className="dcInput"
                                    style={{ width: "100%", fontSize: "13px" }}
                                />
                            </div>
                            <div className="filterGroup" style={{ flexShrink: 0 }}>
                                <button
                                    onClick={handleResetFilters}
                                    className="dcBtn dcBtn--outline"
                                    style={{ padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}
                                >
                                    <RotateCcw size={14} /> Réinitialiser
                                </button>
                            </div>
                        </div>
                        {/* ── Toolbar ── */}
                        <div className="scmToolbar">
                            <div className="scmSearch">
                            </div>
                            <div className="scmActions">
                                <button
                                    className="dcBtn dcBtn--outline"
                                    onClick={handleExport}
                                    disabled={loading}
                                    style={{
                                        background: activeTab === "historique" ? "#10b981" : "",
                                        color: activeTab === "historique" ? "white" : "",
                                        border: activeTab === "historique" ? "none" : "",
                                    }}
                                >
                                    <Download size={15} /> Exporter Excel
                                </button>
                                {activeTab === "soldes" && (
                                    <button
                                        className="dcBtn dcBtn--primary"
                                        onClick={handleImportClick}
                                        disabled={loading}
                                    >
                                        <ArrowUpRight size={15} /> Importer Excel
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ── Tab: Historique ── */}
                        {activeTab === "historique" && (
                            <div className="dcTableWrap">
                                <table className="dcTable">
                                    <thead>
                                    <tr>
                                        <th>Référence</th>
                                        <th>Date de l'opération</th>
                                        <th>Motif</th>
                                        <th>Solde Initial</th>
                                        <th>Solde débit</th>
                                        <th>Solde crédit</th>
                                        <th>Solde Restant</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={7}>
                                                <div className="dcEmpty"><Clock size={24}/><div>Chargement...</div></div>
                                            </td>
                                        </tr>
                                    ) : filteredCombinedHistory.length === 0 ? (
                                        <tr>
                                            <td colSpan={7}>
                                                <div className="dcEmpty"><AlertCircle size={24}/><div>Aucun mouvement trouvé</div></div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredCombinedHistory.map((item, index) => {
                                            const isPositive = item.valeur > 0;
                                            const isNegative = item.valeur < 0;
                                            const isCancelledOrRefused = item.isLeaveRequest &&
                                                (item.statut === "annule" || item.statut === "refuse");

                                            const statusBadge = item.isLeaveRequest ? {
                                                label:
                                                    item.statut === "approuve" ? "Approuvé" :
                                                        item.statut === "refuse"   ? "Refusé"   :
                                                            item.statut === "annule"   ? "Annulé"   :
                                                                item.statut === "reporte"  ? "Reporté"  : item.statut,
                                                bg:
                                                    item.statut === "approuve" ? "#dcfce7" :
                                                        item.statut === "refuse"   ? "#fee2e2" :
                                                            item.statut === "annule"   ? "#f3f4f6" :
                                                                item.statut === "reporte"  ? "#dbeafe" : "#f3f4f6",
                                                color:
                                                    item.statut === "approuve" ? "#16a34a" :
                                                        item.statut === "refuse"   ? "#ef4444" :
                                                            item.statut === "annule"   ? "#6b7280" :
                                                                item.statut === "reporte"  ? "#2563eb" : "#6b7280",
                                            } : null;

                                            return (
                                                <tr
                                                    key={item.id || index}
                                                    style={{
                                                        backgroundColor: isCancelledOrRefused ? "#f9fafb"
                                                            : isPositive  ? "#f0fdf4"
                                                                : isNegative  ? "#fef2f2" : "#fff",
                                                        borderLeft: isCancelledOrRefused ? "3px solid #d1d5db"
                                                            : isPositive  ? "3px solid #16a34a"
                                                                : isNegative  ? "3px solid #ef4444"
                                                                    : "3px solid #6b7280",
                                                        opacity: isCancelledOrRefused ? 0.55 : 1,
                                                    }}
                                                >
                                                    <td>
                                                        <div style={{
                                                            fontWeight: 700,
                                                            color: "#15803d",
                                                            textDecoration: isCancelledOrRefused ? "line-through" : "none"
                                                        }}>
                                                            {item.employeeName || "—"}
                                                        </div>
                                                        <div style={{ fontSize: "11px", color: "#6b7280" }}>
                                                            {item.employeeCode || "—"}{item.entityName ? ` · ${item.entityName}` : ""}
                                                        </div>
                                                    </td>
                                                    <td>{formatDate(item.dateMouvement)}</td>
                                                    <td style={{ fontSize: "12px", color: "#4b5563", maxWidth: "220px" }}>
                                                        <div style={{
                                                            fontWeight: "bold",
                                                            color: "#111827",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            flexWrap: "wrap",
                                                            gap: "6px"
                                                        }}>
                                    <span style={{
                                        textDecoration: isCancelledOrRefused ? "line-through" : "none"
                                    }}>
                                        {item.reference}
                                    </span>
                                                            {statusBadge && (
                                                                <span style={{
                                                                    fontSize: "10px",
                                                                    padding: "1px 6px",
                                                                    borderRadius: "4px",
                                                                    fontWeight: 600,
                                                                    display: "inline-block",
                                                                    backgroundColor: statusBadge.bg,
                                                                    color: statusBadge.color,
                                                                }}>
                                            {statusBadge.label}
                                        </span>
                                                            )}
                                                        </div>
                                                        <div>{item.motif || item.commentaire || "—"}</div>
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: 600 }}>
                                                        {item.soldeInitialSnapshot !== null && item.soldeInitialSnapshot !== undefined
                                                            ? item.soldeInitialSnapshot + "j"
                                                            : "—"}
                                                    </td>
                                                    <td style={{
                                                        textAlign: "center",
                                                        color: "#ef4444",
                                                        fontWeight: 700,
                                                        textDecoration: isCancelledOrRefused ? "line-through" : "none"
                                                    }}>
                                                        {isNegative ? item.valeur + "j" : "—"}
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: 700, color: "#16a34a" }}>
                                                        {isPositive ? "+" + item.valeur + "j" : "—"}
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: 800, background: "#f8fafc" }}>
                                                        {item.soldeRestantSnapshot !== null && item.soldeRestantSnapshot !== undefined
                                                            ? item.soldeRestantSnapshot + "j"
                                                            : "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Tab: Mouvements ── */}
                        {activeTab === "mouvements" && (
                            <div className="dcTableWrap">
                                <table className="dcTable">
                                    <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Matricule</th>
                                        <th>Date</th>
                                        <th>Opération</th>
                                        <th style={{textAlign: "center"}}>Valeur</th>
                                        <th style={{textAlign: "center"}}>Nouveau Solde</th>
                                        <th>Référence / Commentaire</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="7">
                                                <div className="dcEmpty"><Clock size={24}/>
                                                    <div>Chargement...</div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : historyMovements.length === 0 ? (
                                        <tr>
                                            <td colSpan="7">
                                                <div className="dcEmpty"><AlertCircle size={24}/>
                                                    <div>Aucun mouvement trouvé</div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        historyMovements.map((move) => {
                                            const val = move.valeur || 0;
                                            const isPositive = val > 0;
                                            const isNegative = val < 0;

                                            const getTypeColor = (type) => {
                                                switch (type) {
                                                    case "ACCRUAL":
                                                        return "#10b981";
                                                    case "CONSUMPTION":
                                                        return "#ef4444";
                                                    case "CANCELLATION":
                                                        return "#3b82f6";
                                                    case "INITIAL":
                                                        return "#8b5cf6";
                                                    case "ADJUSTMENT":
                                                        return "#f59e0b";
                                                    default:
                                                        return "#6b7280";
                                                }
                                            };

                                            const getTypeLabel = (type) => {
                                                switch (type) {
                                                    case "ACCRUAL":
                                                        return "Acquisition";
                                                    case "CONSUMPTION":
                                                        return "Consommation";
                                                    case "CANCELLATION":
                                                        return "Restitution";
                                                    case "INITIAL":
                                                        return "Initialisation";
                                                    case "ADJUSTMENT":
                                                        return "Ajustement";
                                                    default:
                                                        return type;
                                                }
                                            };

                                            return (
                                                <tr key={move.id}>
                                                    <td>
                                                        <div className="scmEmpCell">
                                                            <div
                                                                className="scmAvatar">{move.employeeName?.charAt(0)}</div>
                                                            <div className="scmEmpName">{move.employeeName}</div>
                                                        </div>
                                                    </td>
                                                    <td>{move.employeeCode}</td>
                                                    <td style={{ fontSize: "13px" }}>{new Date(move.dateMouvement).toLocaleString()}</td>
                                                    <td>
                                                            <span style={{ color: getTypeColor(move.typeMouvement), fontWeight: 600, fontSize: "12px" }}>
                                                                {getTypeLabel(move.typeMouvement)}
                                                            </span>
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: "bold", color: isPositive ? "#10b981" : isNegative ? "#ef4444" : "inherit" }}>
                                                        {isPositive ? "+" : ""}{val}j
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: "500" }}>{move.soldeRestantSnapshot}j</td>
                                                    <td style={{ fontSize: "12px", color: "#6b7280" }}>
                                                        <div style={{ fontWeight: "500", color: "#374151" }}>{move.reference}</div>
                                                        <div>{move.commentaire}</div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Tab: Soldes ── */}
                        {activeTab === "soldes" && (
                            <div className="dcTableWrap">
                                <table className="dcTable">
                                    <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Matricule</th>
                                        <th style={{ textAlign: "center" }}>Solde crédit</th>
                                        <th style={{ textAlign: "center" }}>Solde</th>
                                        <th style={{ textAlign: "center" }}>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5">
                                                <div className="dcEmpty"><Clock size={24} /><div>Chargement...</div></div>
                                            </td>
                                        </tr>
                                    ) : flattenedSoldes.length === 0 ? (
                                        <tr>
                                            <td colSpan="5">
                                                <div className="dcEmpty"><AlertCircle size={24} /><div>Aucun solde trouvé</div></div>
                                            </td>
                                        </tr>
                                    ) : (
                                        flattenedSoldes.map((solde, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <div className="scmEmpCell">
                                                        <div className="scmAvatar">{solde.userFullName?.charAt(0)}</div>
                                                        <div>
                                                            <div className="scmEmpName">{solde.userFullName}</div>
                                                            {solde.entityName && (
                                                                <div style={{ fontSize: "11px", color: "#6b7280" }}>
                                                                    {solde.entityName}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{solde.userEmployeeCode}</td>
                                                <td style={{ textAlign: "center" }}>
                                                        <span className="scmTotalSolde" style={{ color: "#2563eb" }}>
                                                            {solde.soldeAjoute || 0} <span className="dcTiny">jours</span>
                                                        </span>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                        <span className="scmTotalSolde" style={{ color: solde.soldeRestant < 0 ? "#ef4444" : "#16a34a" }}>
                                                            {solde.soldeRestant} <span className="dcTiny">jours</span>
                                                        </span>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    <button
                                                        className="dcBtn dcBtn--icon dcBtn--primary"
                                                        style={{ padding: "6px", borderRadius: "8px" }}
                                                        onClick={() => handleOpenAdjust(solde)}
                                                        title="Ajouter des jours"
                                                    >
                                                        <PlusCircle size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* ══════════════════════════════════════════
                    MODALE : Alimenter les soldes (groupé)
                ══════════════════════════════════════════ */}
                {bulkModalOpen && (
                    <div className="tnfModal">
                        <div className="tnfModal__panel" role="dialog" aria-modal="true" style={{ maxWidth: 620, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                            <div className="tnfModal__header"><div className="tnfModal__headLeft"><div className="tnfModal__icon"><Wallet size={18} /></div><div><div className="tnfModal__title">Alimenter les soldes</div><div className="tnfModal__sub">Ajout ou déduction groupée</div></div></div><button className="tnfIconBtn" type="button" onClick={() => setBulkModalOpen(false)}><X size={18} /></button></div>
                            <div className="tnfModal__body" style={{ overflowY: "auto", flex: 1 }}>
                                {bulkError && <div style={{ color: "#b91c1c", background: "#fef2f2", padding: 10, borderRadius: 8, marginBottom: 14 }}>{bulkError}</div>}
                                <div className="tnfPanel" style={{ marginBottom: 16 }}>
                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>Type de congé <span className="tnfReq">*</span></label>
                                    <select className="dcInput" style={{ width: "100%", marginBottom: 14 }} value={bulkAbsenceTypeId} onChange={(e) => { setBulkAbsenceTypeId(e.target.value); setBulkError(""); }}><option value="">Sélectionner un type</option>{typesSoldesDisponibles.map((type) => <option key={type.id} value={type.id}>{type.label}</option>)}</select>
                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>Nombre de jours <span style={{ fontSize: 11, color: "#6b7280" }}>(ex. 0.5 ; valeur négative pour déduire)</span></label>
                                    <input type="number" step="0.01" className="dcInput" style={{ width: "100%" }} placeholder="Ex : 1.5 ou -0.5" value={bulkDays} onChange={(e) => { setBulkDays(e.target.value); setBulkError(""); }} />
                                </div>
                                <div className="tnfPanel"><div className="tnfPanel__title"><User size={16} /> Collaborateurs <span className="tnfReq">*</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10, fontSize: 13 }}><label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}><input type="checkbox" checked={bulkSelectedUserIds.length === bulkUsers.length && bulkUsers.length > 0} onChange={(e) => setBulkSelectedUserIds(e.target.checked ? bulkUsers.map((user) => user.id) : [])} /> Tout sélectionner</label><span style={{ color: "#64748b" }}>{bulkSelectedUserIds.length} sélectionné(s)</span></div>
                                    <input className="dcInput" style={{ width: "100%", marginBottom: 10 }} placeholder="Rechercher un collaborateur..." value={bulkUserSearch} onChange={(e) => setBulkUserSearch(e.target.value)} />
                                    <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>{filteredBulkUsers.map((user) => <label key={user.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 12px", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}><input type="checkbox" checked={bulkSelectedUserIds.includes(user.id)} onChange={(e) => setBulkSelectedUserIds((ids) => e.target.checked ? [...ids, user.id] : ids.filter((id) => id !== user.id))} /><span>{user.fullName} <span style={{ color: "#64748b" }}>— {user.employeeCode || "Sans matricule"}</span></span></label>)}</div>
                                    <label className="tnfLabel" style={{ display: "block", margin: "16px 0 6px" }}>Motif</label><select className="dcInput" style={{ width: "100%", marginBottom: 10 }} value={bulkMotif} onChange={(e) => setBulkMotif(e.target.value)}><option value="">Ajustement groupé</option>{MOTIFS_PREDEFINED.map((motif) => <option key={motif.value} value={motif.value}>{motif.label}</option>)}</select><textarea className="tnfTextarea" rows={2} style={{ width: "100%" }} placeholder="Commentaire (facultatif)" value={bulkComment} onChange={(e) => setBulkComment(e.target.value)} />
                                </div>
                            </div>
                            <div className="tnfModal__footer"><button className="tnfBtn tnfBtn--ghost" type="button" onClick={() => setBulkModalOpen(false)}>Annuler</button><button className="tnfBtn tnfBtn--ok" type="button" onClick={handleBulkAdjustmentSubmit}><Save size={16} /> Enregistrer</button></div>
                        </div>
                    </div>
                )}

                {/* ══════════════════════════════════════════
                    MODALE : Ajustement du solde
                ══════════════════════════════════════════ */}
                {adjustModalOpen && (
                    <div className="tnfModal">
                        <div className="tnfModal__panel" role="dialog" aria-modal="true" style={{ maxWidth: 520, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                            <div className="tnfModal__header">
                                <div className="tnfModal__headLeft">
                                    <div className="tnfModal__icon">
                                        <PlusCircle size={18} />
                                    </div>
                                    <div>
                                        <div className="tnfModal__title">Ajustement du solde</div>
                                        <div className="tnfModal__sub">
                                            {selectedSolde?.absenceTypeNom} — {selectedSolde?.annee}
                                        </div>
                                    </div>
                                </div>
                                <button className="tnfIconBtn" type="button" onClick={handleCloseAdjust}>
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="tnfModal__body" style={{ overflowY: "auto", flex: 1 }}>
                                <div className="tnfPanel" style={{ marginBottom: 20 }}>
                                    <div className="tnfPanel__title">
                                        <User size={16} /> Collaborateur
                                    </div>
                                    <div className="tnfKv">
                                        <span>Nom</span>
                                        <b>{selectedSolde?.userFullName}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Matricule</span>
                                        <b>{selectedSolde?.userEmployeeCode}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Type de congé</span>
                                        <b>{selectedSolde?.absenceTypeNom}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Solde restant</span>
                                        <b style={{ color: selectedSolde?.soldeRestant < 0 ? "#ef4444" : "#16a34a" }}>
                                            {selectedSolde?.soldeRestant} jours
                                        </b>
                                    </div>
                                </div>

                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <TrendingUp size={16} /> Ajustement
                                    </div>

                                    {/* Nombre de jours */}
                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                        Nombre de jours <span style={{ fontSize: 11, color: "#6b7280" }}>(valeur négative pour déduire)</span>
                                    </label>
                                    <div style={{ position: "relative", marginBottom: 20 }}>
                                        <input
                                            type="number"
                                            className="dcInput"
                                            style={{ width: "100%", paddingRight: 48 }}
                                            placeholder="Ex: 1.5, -2, ..."
                                            value={daysToAdd}
                                            onChange={(e) => setDaysToAdd(e.target.value)}
                                            autoFocus
                                        />
                                        <div style={{
                                            position: "absolute", right: 12, top: "50%",
                                            transform: "translateY(-50%)", color: "#9ca3af", fontSize: 12
                                        }}>
                                            jours
                                        </div>
                                    </div>

                                    {/* Motif prédéfini */}
                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 10 }}>
                                        Motif de l'ajustement
                                    </label>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
                                        {MOTIFS_PREDEFINED.map((m) => {
                                            const isChecked = motifType === m.value;
                                            return (
                                                <label
                                                    key={m.value}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 10,
                                                        padding: "9px 14px",
                                                        borderRadius: 8,
                                                        border: `2px solid ${isChecked ? "#204385" : "#e5e7eb"}`,
                                                        background: isChecked ? "#eff6ff" : "#fafafa",
                                                        cursor: "pointer",
                                                        transition: "all 0.15s ease",
                                                        fontSize: 13,
                                                        fontWeight: isChecked ? 600 : 400,
                                                        color: isChecked ? "#204385" : "#374151",
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="motifType"
                                                        value={m.value}
                                                        checked={isChecked}
                                                        onChange={() => setMotifType(m.value)}
                                                        style={{ accentColor: "#204385", width: 16, height: 16, cursor: "pointer" }}
                                                    />
                                                    {m.label}
                                                </label>
                                            );
                                        })}
                                        {/* Option "Autre" pour décocher */}
                                        {motifType && (
                                            <button
                                                type="button"
                                                onClick={() => setMotifType("")}
                                                style={{
                                                    alignSelf: "flex-start",
                                                    fontSize: 11,
                                                    color: "#9ca3af",
                                                    background: "none",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    textDecoration: "underline",
                                                    padding: 0,
                                                }}
                                            >
                                                Effacer la sélection
                                            </button>
                                        )}
                                    </div>

                                    {/* Commentaire libre */}
                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                        Commentaire libre
                                    </label>
                                    <textarea
                                        className="tnfTextarea"
                                        rows={3}
                                        placeholder="Précisez le contexte, la période concernée, toute information utile..."
                                        value={commentaireLibre}
                                        onChange={(e) => setCommentaireLibre(e.target.value)}
                                        style={{ width: "100%", resize: "vertical" }}
                                    />
                                </div>
                            </div>

                            <div className="tnfModal__footer">
                                <button className="tnfBtn tnfBtn--ghost" type="button" onClick={handleCloseAdjust}>
                                    Annuler
                                </button>
                                <button
                                    className="tnfBtn tnfBtn--ok"
                                    type="button"
                                    onClick={handleSaveAdjustment}
                                    disabled={!daysToAdd}
                                >
                                    <Save size={16} /> Enregistrer
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ══════════════════════════════════════════
                    MODALE : Saisir un congé
                ══════════════════════════════════════════ */}
                {saisirModalOpen && (
                    <div className="tnfModal">
                        <div
                            className="tnfModal__panel"
                            role="dialog"
                            aria-modal="true"
                            style={{ maxWidth: 620, maxHeight: "90vh", display: "flex", flexDirection: "column" }}
                        >
                            <div className="tnfModal__header">
                                <div className="tnfModal__headLeft">
                                    <div className="tnfModal__icon">
                                        <PlusCircle size={18} />
                                    </div>
                                    <div>
                                        <div className="tnfModal__title">Saisir un congé</div>
                                        <div className="tnfModal__sub">Enregistrer un congé pour un collaborateur</div>
                                    </div>
                                </div>
                                <button className="tnfIconBtn" type="button" onClick={handleCloseSaisir}>
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="tnfModal__body" style={{ overflowY: "auto", flex: 1 }}>

                                {saisirError && (
                                    <div className="dcAlert" style={{ marginBottom: 16 }}>
                                        <AlertCircle size={16} color="#9f1239" />
                                        <div style={{ fontSize: 13 }}>{saisirError}</div>
                                    </div>
                                )}

                                {/* Détails du congé */}
                                <div className="tnfPanel" style={{ marginBottom: 16 }}>
                                    <div className="tnfPanel__title">
                                        <CalendarDays size={16} /> Détails du congé
                                    </div>

                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                        Type de congé <span className="tnfReq">*</span>
                                    </label>
                                    <select
                                        className="dcInput"
                                        style={{ width: "100%", marginBottom: 14 }}
                                        value={saisirForm.absenceTypeId}
                                        onChange={(e) => setSaisirForm({
                                            ...saisirForm,
                                            absenceTypeId: e.target.value,
                                            justificatif: null,
                                            autorisation: null,
                                        })}
                                    >
                                        <option value="">Sélectionner un type</option>
                                        {typesCongesDisponibles.map((t) => (
                                            <option key={t.id} value={t.id}>{t.label}</option>
                                        ))}
                                    </select>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                                        <div>
                                            <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                                Date de début <span className="tnfReq">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="dcInput"
                                                style={{ width: "100%" }}
                                                value={saisirForm.dateDebut}
                                                onChange={(e) => setSaisirForm({ ...saisirForm, dateDebut: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                                Date de fin <span className="tnfReq">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="dcInput"
                                                style={{ width: "100%" }}
                                                value={saisirForm.dateFin}
                                                onChange={(e) => setSaisirForm({ ...saisirForm, dateFin: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    {saisirForm.dateDebut && saisirForm.dateFin && dureeCalculee > 0 && (
                                        <div className="tnfMiniCard" style={{ marginBottom: 14 }}>
                                            <div className="tnfMiniRow">
                                                <span className="tnfMuted">Durée calculée</span>
                                                <b className="tnfAccent">{dureeCalculee} jour{dureeCalculee > 1 ? "s" : ""}</b>
                                            </div>
                                        </div>
                                    )}

                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                        Commentaire / Motif
                                    </label>
                                    <textarea
                                        className="tnfTextarea"
                                        placeholder="Précisez le motif du congé..."
                                        value={saisirForm.commentaire}
                                        onChange={(e) => setSaisirForm({ ...saisirForm, commentaire: e.target.value })}
                                        rows={3}
                                    />
                                </div>

                                {/* Justificatif conditionnel */}
                                {saisirTypeObj?.requireJustificatif && (
                                    <div className="tnfPanel" style={{ marginBottom: 16 }}>
                                        <div className="tnfPanel__title">
                                            <FileText size={16} /> Documents
                                        </div>
                                        <label className="tnfLabel" style={{ display: "block", marginBottom: 8 }}>
                                            {saisirTypeObj.justificationType}
                                            {saisirTypeObj.justificatifMandatory && <span className="tnfReq"> *</span>}
                                        </label>
                                        <div style={{
                                            border: "2px dashed #d1d5db", borderRadius: 10,
                                            padding: 16, textAlign: "center", background: "#f9fafb"
                                        }}>
                                            <input
                                                type="file" id="saisir-justif"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                style={{ display: "none" }}
                                                onChange={(e) => setSaisirForm({ ...saisirForm, justificatif: e.target.files?.[0] || null })}
                                            />
                                            <label htmlFor="saisir-justif" className="tnfBtn tnfBtn--ghost" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                                                <Upload size={14} /> Parcourir
                                            </label>
                                            {saisirForm.justificatif && (
                                                <div style={{ marginTop: 8, fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
                                                    ✓ {saisirForm.justificatif.name}
                                                </div>
                                            )}
                                            <div className="tnfHelp" style={{ marginTop: 6 }}>PDF, JPG, PNG (max 5Mo)</div>
                                        </div>
                                    </div>
                                )}

                                {/* Autorisation conditionnelle */}
                                {saisirTypeObj?.requireAutorisation && (
                                    <div className="tnfPanel" style={{ marginBottom: 16 }}>
                                        <div className="tnfPanel__title">
                                            <FileText size={16} /> Autorisation
                                        </div>
                                        <div style={{
                                            border: "2px dashed #fca5a5", borderRadius: 10,
                                            padding: 16, textAlign: "center", background: "#fff5f5"
                                        }}>
                                            <input
                                                type="file" id="saisir-auto"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                style={{ display: "none" }}
                                                onChange={(e) => setSaisirForm({ ...saisirForm, autorisation: e.target.files?.[0] || null })}
                                            />
                                            <label htmlFor="saisir-auto" className="tnfBtn tnfBtn--ghost" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, borderColor: "#ef4444", color: "#ef4444" }}>
                                                <Upload size={14} /> Parcourir
                                            </label>
                                            {saisirForm.autorisation && (
                                                <div style={{ marginTop: 8, fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
                                                    ✓ {saisirForm.autorisation.name}
                                                </div>
                                            )}
                                            <div className="tnfHelp" style={{ marginTop: 6 }}>PDF, JPG, PNG (max 5Mo)</div>
                                        </div>
                                    </div>
                                )}

                                {/* Sélection collaborateur */}
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <User size={16} /> Collaborateur <span className="tnfReq">*</span>
                                    </div>

                                    {selectedUser && (
                                        <div className="tnfMiniCard" style={{ marginBottom: 12, borderColor: "#204385", background: "#eff6ff" }}>
                                            <div className="tnfMiniRow">
                                                <span className="tnfMuted">Sélectionné</span>
                                                <b style={{ color: "#204385", display: "flex", alignItems: "center", gap: 6 }}>
                                                    <CheckCircle size={14} color="#204385" />
                                                    {selectedUser.fullName} — {selectedUser.employeeCode}
                                                </b>
                                            </div>
                                        </div>
                                    )}

                                    <div className="tnfHelp" style={{ marginBottom: 12, display: "flex", gap: 6, alignItems: "start", color: "#64748b" }}>
                                        <ShieldCheck size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <span>Seuls les collaborateurs ayant activé la délégation dans leurs paramètres sont visibles ici.</span>
                                    </div>

                                    <div style={{ position: "relative", marginBottom: 10 }}>
                                        <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                                        <input
                                            type="text"
                                            className="dcInput"
                                            placeholder="Rechercher un collaborateur..."
                                            value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                            style={{ width: "100%", paddingLeft: 32, fontSize: 13 }}
                                        />
                                    </div>

                                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                                        {filteredUsers.length === 0 ? (
                                            <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 12, padding: "16px 8px", lineHeight: "1.5" }}>
                                                {userSearch
                                                    ? "Aucun collaborateur trouvé"
                                                    : "Aucun collaborateur n'a encore activé la délégation de saisie de congés."}
                                            </div>

                                        ) : (
                                            filteredUsers.map((user) => {
                                                const isSelected = String(saisirForm.selectedUserId) === String(user.id);
                                                return (
                                                    <div
                                                        key={user.id}
                                                        onClick={() => setSaisirForm({ ...saisirForm, selectedUserId: user.id })}
                                                        style={{
                                                            display: "flex", alignItems: "center", gap: 10,
                                                            padding: "10px 12px", borderRadius: 10,
                                                            border: `2px solid ${isSelected ? "#204385" : "#e5e7eb"}`,
                                                            background: isSelected ? "#eff6ff" : "#fff",
                                                            cursor: "pointer", transition: "all 0.15s ease",
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: 34, height: 34, borderRadius: "50%",
                                                            background: isSelected ? "#204385" : "#e5e7eb",
                                                            color: isSelected ? "#fff" : "#374151",
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            fontWeight: 700, fontSize: 13, flexShrink: 0,
                                                        }}>
                                                            {user.fullName?.charAt(0)?.toUpperCase()}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{user.fullName}</div>
                                                            <div style={{ fontSize: 11, color: "#6b7280" }}>{user.employeeCode}</div>
                                                        </div>
                                                        {isSelected && <CheckCircle size={16} color="#204385" />}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="tnfModal__footer">
                                {saisirForm.selectedUserId && saisirForm.absenceTypeId && saisirForm.dateDebut && saisirForm.dateFin && (
                                    <div style={{ flex: 1, fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 6 }}>
                                        <CheckCircle size={14} color="#10b981" />
                                        {selectedUser?.fullName} · {typesCongesDisponibles.find(t => String(t.id) === String(saisirForm.absenceTypeId))?.label} · {dureeCalculee}j
                                    </div>
                                )}
                                <button className="tnfBtn tnfBtn--ghost" type="button" onClick={handleCloseSaisir}>
                                    Annuler
                                </button>
                                <button
                                    className="tnfBtn tnfBtn--ok"
                                    type="button"
                                    onClick={handleSaisirSubmit}
                                    disabled={
                                        !saisirForm.absenceTypeId ||
                                        !saisirForm.dateDebut ||
                                        !saisirForm.dateFin ||
                                        !saisirForm.selectedUserId
                                    }
                                >
                                    <Save size={16} /> Enregistrer
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SoldeCongesManagement;
